import {
  Alert,
  Avatar,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  CalendarClock,
  MailX,
  MoreVertical,
  PauseCircle,
  PlayCircle,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users2,
} from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useInvitations, useMembers, useMemberships, useRevokeInvitation } from '@/api/queries'
import type { Invitation, Member } from '@/api/types'
import { MEMBERSHIP_STATUSES, ORG_ROLES } from '@/api/types'
import { CursorPager } from '@/components/data/cursor-pager'
import { PageHeader } from '@/components/layout/app-shell'
import {
  InvitationStatusBadge,
  LocationsBadge,
  MemberStatusBadge,
  OrgRoleBadge,
} from '@/features/members/member-badges'
import {
  ChangeRoleDialog,
  RemoveMemberDialog,
  SuspendMemberDialog,
} from '@/features/members/member-dialogs'
import { InviteMemberDialog } from '@/features/members/invite-member-dialog'
import { canManageRole } from '@/features/members/roles'
import {
  ORG_ROLE_LABELS,
  avatarName,
  formatDateTime,
  formatRelative,
  membershipRoleLabel,
} from '@/lib/format'
import { notify } from '@/lib/toast'
import { useCursorPagination } from '@/lib/use-cursor-pagination'
import { cn } from '@/lib/utils'
import { useSelectedOrganizationId } from '@/org/use-organization'
import { StaffAvailabilityDialog } from '@/features/agenda/staff-availability-dialog'
import { RolesPanel } from '@/features/organization/roles-panel'

/**
 * Operatori e inviti — OPS-05.
 *
 * ## Perché è una pagina separata da «Utenti»
 *
 * Sono due assi diversi (OPS-01) e vanno guardati separatamente. `/utenti` è il
 * perimetro di **piattaforma**: tutti gli account di Gymmi, con il loro ruolo
 * `user | admin`, e le azioni sono di moderazione. Questa pagina è il perimetro
 * di **organizzazione**: chi lavora nella palestra attiva, con che ruolo, e le
 * azioni sono di gestione. La stessa persona compare in entrambe con ruoli
 * diversi, ed è corretto che sia così.
 *
 * ## Il tenant è implicito
 *
 * Nessuna di queste chiamate porta un `organizationId`: l'ambito viene
 * dall'header `X-Organization-Id`, cioè da ciò che il selettore di TEN-06 ha
 * scelto. Cambiare organizzazione svuota la cache e questa pagina si ricarica da
 * sola con gli operatori giusti — motivo per cui l'organizzazione attiva è
 * scritta in cima, e non solo nel selettore in sidebar.
 */
const searchSchema = z.object({
  tab: z.enum(['operatori', 'inviti']).optional(),
  role: z.enum(ORG_ROLES).optional(),
  status: z.enum(MEMBERSHIP_STATUSES).optional(),
})

type MemberSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/operatori/')({
  validateSearch: searchSchema,
  component: MembersPage,
})

const PAGE_SIZE = 25

function MembersPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const [inviting, setInviting] = useState(false)
  const [changingRole, setChangingRole] = useState<Member | null>(null)
  const [suspending, setSuspending] = useState<Member | null>(null)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [availabilityOf, setAvailabilityOf] = useState<Member | null>(null)

  const selectedOrganizationId = useSelectedOrganizationId()
  const memberships = useMemberships()

  /**
   * L'appartenenza con cui stiamo operando.
   *
   * Senza selezione esplicita non è "nessuna organizzazione": è quella che il
   * server risolve da sé, e `current` dice quale — la stessa cascata vale per
   * questa pagina, che manda le sue richieste senza header esattamente come la
   * chiamata da cui l'elenco arriva. Cercarla solo per selezione lasciava `actor`
   * indefinito nel caso **più comune** (nessuna scelta mai fatta, o una sola
   * appartenenza, dove il selettore non compare nemmeno): «(tu)» non sarebbe
   * comparso accanto alla propria riga e i controlli sarebbero rimasti permissivi
   * anche potendo sapere.
   *
   * Con una selezione che non corrisponde a niente `actor` resta indefinito di
   * proposito: quella selezione viaggia comunque nell'header e le richieste
   * falliscono con `403`, e ripiegare su `current` mostrerebbe i permessi di
   * un'organizzazione diversa da quella che il server sta rifiutando.
   */
  const actor = memberships.data?.find((item) =>
    selectedOrganizationId === null ? item.current : item.organizationId === selectedOrganizationId,
  )

  const params = {
    ...(search.role ? { role: search.role } : {}),
    ...(search.status ? { status: search.status } : {}),
    limit: PAGE_SIZE,
  }

  // Stessa paginazione a cursore delle altre liste. `meta.total` non arriva —
  // `listMembers` restituisce solo `nextCursor` — e `CursorPager` lo gestisce
  // già mostrando le righe della pagina invece di un totale che non ha.
  const pagination = useCursorPagination(JSON.stringify(params))
  const list = useMembers({
    ...params,
    ...(pagination.cursor ? { cursor: pagination.cursor } : {}),
  })
  const invitations = useInvitations()

  const setFilter = <K extends keyof MemberSearch>(key: K, value: MemberSearch[K]) => {
    void navigate({
      search: (previous) => {
        const next = { ...previous }
        if (value === undefined) delete next[key]
        else next[key] = value
        return next
      },
    })
  }

  const rows = list.data?.data ?? []
  const openInvitations = invitations.data ?? []
  const tab = search.tab ?? 'operatori'

  return (
    <>
      <PageHeader
        title="Operatori"
        description={
          actor ? (
            <>
              {actor.organizationName} — sei <strong>{membershipRoleLabel(actor)}</strong>. Ruoli,
              sospensioni e inviti valgono solo per questa organizzazione.
            </>
          ) : (
            <>
              Ruoli, sospensioni e inviti dell'organizzazione attiva. L'account di piattaforma si
              gestisce da{' '}
              <Link to="/utenti" className="underline underline-offset-4">
                Utenti
              </Link>
              .
            </>
          )
        }
        actions={
          <Button variant="primary" onClick={() => setInviting(true)}>
            <UserPlus />
            Invita
          </Button>
        }
      />

      {/* `403 mfa_setup_required` non è un permesso mancante: è il secondo fattore
          che manca, e la differenza è tutta nell'azione da fare dopo. */}
      {list.error instanceof ApiError && list.error.isMfaSetupRequired ? (
        <Alert variant="warning" className="mb-4" title="Serve la verifica in due passaggi">
          <p>{list.error.message}</p>
          <Link
            to="/impostazioni"
            className="text-primary-content mt-1 inline-flex underline underline-offset-4"
          >
            Vai a configurarla
          </Link>
        </Alert>
      ) : null}

      <Tabs
        variant="pill"
        value={tab}
        onValueChange={(value) => setFilter('tab', value as MemberSearch['tab'])}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="operatori">Operatori</TabsTrigger>
          <TabsTrigger value="inviti">
            Inviti aperti
            {openInvitations.length > 0 ? ` (${openInvitations.length})` : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="operatori" className="mt-0">
          <div className="mb-4 grid grid-cols-2 gap-2 sm:flex">
            <Select
              aria-label="Filtra per ruolo"
              value={search.role ?? 'all'}
              onValueChange={(value) =>
                setFilter('role', value === 'all' ? undefined : (value as MemberSearch['role']))
              }
              className="sm:w-44"
              items={[
                { value: 'all', label: 'Ogni ruolo' },
                ...ORG_ROLES.map((role) => ({ value: role, label: ORG_ROLE_LABELS[role] })),
              ]}
            />

            <Select
              aria-label="Filtra per stato"
              value={search.status ?? 'all'}
              onValueChange={(value) =>
                setFilter('status', value === 'all' ? undefined : (value as MemberSearch['status']))
              }
              className="sm:w-40"
              items={[
                { value: 'all', label: 'Ogni stato' },
                { value: 'active', label: 'Attivi' },
                { value: 'invited', label: 'Invitati' },
                { value: 'suspended', label: 'Sospesi' },
              ]}
            />
          </div>

          {list.isError && !list.data ? (
            <ErrorState
              message={errorMessage(list.error)}
              requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
              onRetry={() => void list.refetch()}
            />
          ) : list.isPending ? (
            <ListSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Users2 />}
              title="Nessun operatore"
              description="Invita chi lavora in questa organizzazione: riceverà un link via email."
              action={
                <Button variant="primary" onClick={() => setInviting(true)}>
                  <UserPlus />
                  Invita
                </Button>
              }
            />
          ) : (
            <>
              <div className={cn('transition-opacity', list.isFetching && 'opacity-60')}>
                <ul className="flex flex-col gap-2 lg:hidden">
                  {rows.map((member) => (
                    <MemberCard
                      key={member.memberId}
                      member={member}
                      isSelf={member.memberId === actor?.memberId}
                      canManage={canManageRole(actor?.role, member.role)}
                      onChangeRole={() => setChangingRole(member)}
                      onSuspend={() => setSuspending(member)}
                      onRemove={() => setRemoving(member)}
                      onAvailability={() => setAvailabilityOf(member)}
                    />
                  ))}
                </ul>

                <div className="border-border bg-card hidden rounded-lg border lg:block">
                  <Table>
                    <TableHead>
                      <TableRow className="hover:bg-transparent">
                        <TableHeaderCell>Operatore</TableHeaderCell>
                        <TableHeaderCell>Ruolo</TableHeaderCell>
                        <TableHeaderCell>Stato</TableHeaderCell>
                        <TableHeaderCell>Sedi</TableHeaderCell>
                        <TableHeaderCell>Nell'organizzazione da</TableHeaderCell>
                        <TableHeaderCell className="w-12">
                          <span className="sr-only">Azioni</span>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((member) => (
                        <MemberRow
                          key={member.memberId}
                          member={member}
                          isSelf={member.memberId === actor?.memberId}
                          canManage={canManageRole(actor?.role, member.role)}
                          onChangeRole={() => setChangingRole(member)}
                          onSuspend={() => setSuspending(member)}
                          onRemove={() => setRemoving(member)}
                          onAvailability={() => setAvailabilityOf(member)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <CursorPager
                meta={list.data.meta}
                page={pagination.page}
                pageCount={rows.length}
                canGoBack={pagination.canGoBack}
                onNext={() => pagination.next(list.data?.meta.nextCursor)}
                onPrevious={pagination.previous}
                isFetching={list.isFetching}
                itemLabel="operatori"
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="inviti" className="mt-0">
          <InvitationsPanel
            invitations={openInvitations}
            isPending={invitations.isPending}
            isError={invitations.isError}
            error={invitations.error}
            onRetry={() => void invitations.refetch()}
            onInvite={() => setInviting(true)}
          />
        </TabsContent>
      </Tabs>

      {/* ⚠️ Qui stava un avviso che diceva «i ruoli sono quelli predefiniti», perché
          `/organization/roles` esisteva nel backend e non nel contract. Era vero quando è
          stato scritto e ha smesso di esserlo il **13 agosto**, quando le dodici rotte
          arretrate sono entrate nel contract: da allora il blocco non c'era più, e la nota
          che descriveva l'attesa è sopravvissuta all'attesa per una settimana.
          È la malattia che §1 del piano dichiara, con l'aggravante che viveva nel codice —
          dove nessuna rilettura del piano poteva trovarla. L'ha trovata l'audit delle
          operazioni irraggiungibili (§8bis), non una persona che rileggeva. */}
      <div className="mt-6">
        <h2 className="typo-label-lg mb-2 font-semibold">Ruoli personalizzati</h2>
        <RolesPanel canWrite={actor?.role === 'owner' || actor?.role === 'manager'} />
      </div>

      <InviteMemberDialog open={inviting} onOpenChange={setInviting} actorRole={actor?.role} />
      <ChangeRoleDialog
        member={changingRole}
        actorRole={actor?.role}
        open={changingRole !== null}
        onOpenChange={(open) => !open && setChangingRole(null)}
      />
      <SuspendMemberDialog
        member={suspending}
        open={suspending !== null}
        onOpenChange={(open) => !open && setSuspending(null)}
      />
      <RemoveMemberDialog
        member={removing}
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
      />
      {availabilityOf !== null ? (
        <StaffAvailabilityDialog
          open
          onOpenChange={(open) => !open && setAvailabilityOf(null)}
          memberId={availabilityOf.memberId}
          memberName={availabilityOf.name ?? availabilityOf.email}
          canManage={actor?.role === 'owner' || actor?.role === 'manager'}
        />
      ) : null}
    </>
  )
}

// ── riga e card ───────────────────────────────────────────────────────────────

interface RowProps {
  member: Member
  /** La propria appartenenza: il server rifiuta sospensione e rimozione di sé stessi. */
  isSelf: boolean
  /** `false` se il proprio ruolo non arriva a quello del bersaglio. */
  canManage: boolean
  onChangeRole: () => void
  onSuspend: () => void
  onRemove: () => void
  onAvailability: () => void
}

function MemberRow(props: RowProps) {
  const { member } = props
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar name={avatarName(member.name ?? member.email)} size="md" />
          <div className="min-w-0">
            <p className="max-w-xs truncate text-sm font-medium">
              {member.name ?? member.email.split('@')[0]}
              {props.isSelf ? <span className="text-muted-foreground"> (tu)</span> : null}
            </p>
            <p className="text-muted-foreground max-w-xs truncate text-xs">{member.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <OrgRoleBadge member={member} />
      </TableCell>
      <TableCell>
        <MemberStatusBadge status={member.status} />
      </TableCell>
      <TableCell>
        <LocationsBadge locationIds={member.locationIds} />
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {/* `joinedAt` è `null` finché l'invito non è stato accettato: la data di
            creazione dell'appartenenza non è la stessa cosa. */}
        {member.joinedAt ? formatRelative(member.joinedAt) : 'non ancora entrato'}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <RowMenu {...props} />
        </div>
      </TableCell>
    </TableRow>
  )
}

function MemberCard(props: RowProps) {
  const { member } = props
  return (
    <li>
      <Card radius="md" padding="sm">
        <div className="flex items-start gap-3">
          <Avatar name={avatarName(member.name ?? member.email)} size="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {member.name ?? member.email.split('@')[0]}
              {props.isSelf ? <span className="text-muted-foreground"> (tu)</span> : null}
            </p>
            <p className="text-muted-foreground truncate text-xs">{member.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <OrgRoleBadge member={member} />
              <MemberStatusBadge status={member.status} />
              <LocationsBadge locationIds={member.locationIds} />
            </div>
          </div>
          <RowMenu {...props} />
        </div>
      </Card>
    </li>
  )
}

/**
 * Menu di riga.
 *
 * Le voci che il server rifiuterebbe sono **disabilitate**, non nascoste: una
 * voce assente lascia credere che l'azione non esista, una disabilitata dice che
 * esiste ma non spetta a te. Il controllo resta comunque lato server — qui si
 * evita solo di far scoprire da un errore ciò che si poteva dire prima.
 */
function RowMenu({
  member,
  isSelf,
  canManage,
  onChangeRole,
  onSuspend,
  onRemove,
  onAvailability,
}: RowProps) {
  const suspended = member.status === 'suspended'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon-sm" variant="ghost" aria-label={`Azioni su ${member.email}`}>
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuItem disabled={!canManage} onSelect={onChangeRole}>
          <ShieldCheck />
          Cambia ruolo
        </DropdownMenuItem>

        {/* La disponibilità è di **chiunque** lavori qui, non solo dei professionisti:
            l'agenda propone spazi a chi ha una settimana tipo dichiarata, e chi non
            l'ha non compare fra le scelte — un'assenza che si scopre cercando un buco
            che non c'è. Aperta anche a chi non può scrivere: leggere quando lavora un
            collega non è amministrare. */}
        <DropdownMenuItem onSelect={onAvailability}>
          <CalendarClock />
          Disponibilità e assenze
        </DropdownMenuItem>

        {/* Sospendersi da soli ha lo stesso effetto di togliersi l'accesso, e la
            protezione sull'ultimo titolare non lo coprirebbe quando i titolari
            sono due: il server risponde `409 cannot_modify_self`. */}
        <DropdownMenuItem disabled={!canManage || isSelf} onSelect={onSuspend}>
          {suspended ? <PlayCircle /> : <PauseCircle />}
          {suspended ? 'Riattiva' : 'Sospendi'}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem destructive disabled={!canManage || isSelf} onSelect={onRemove}>
          <UserMinus />
          Rimuovi dall'organizzazione
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── inviti aperti ─────────────────────────────────────────────────────────────

/**
 * Gli inviti non sono paginati: `GET /organization/invitations` restituisce un
 * array con i soli inviti **aperti**, che è l'elenco utile. Un invito già
 * accettato è diventato un'appartenenza e si guarda nell'altra scheda; uno
 * revocato non serve più a nessuno.
 */
function InvitationsPanel({
  invitations,
  isPending,
  isError,
  error,
  onRetry,
  onInvite,
}: {
  invitations: Invitation[]
  isPending: boolean
  isError: boolean
  error: unknown
  onRetry: () => void
  onInvite: () => void
}) {
  const revoke = useRevokeInvitation()

  const onRevoke = async (invitation: Invitation) => {
    try {
      await revoke.mutateAsync(invitation.invitationId)
      notify({ message: 'Invito revocato.', variant: 'success' })
    } catch (revokeError) {
      notify({ message: errorMessage(revokeError), variant: 'danger' })
    }
  }

  if (isError) {
    return (
      <ErrorState
        message={errorMessage(error)}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={onRetry}
      />
    )
  }

  if (isPending) return <ListSkeleton />

  if (invitations.length === 0) {
    return (
      <EmptyState
        icon={<MailX />}
        title="Nessun invito aperto"
        description="Gli inviti accettati diventano appartenenze e si vedono fra gli operatori."
        action={
          <Button variant="outline" onClick={onInvite}>
            <UserPlus />
            Invita qualcuno
          </Button>
        }
      />
    )
  }

  return (
    <ul className="flex flex-col gap-2">
      {invitations.map((invitation) => (
        <li key={invitation.invitationId}>
          <Card radius="md" padding="sm">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{invitation.email}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <OrgRoleBadge member={invitation} />
                  <InvitationStatusBadge invitation={invitation} />
                </div>
                <p className="text-muted-foreground mt-1.5 text-xs">
                  Inviato {formatRelative(invitation.createdAt)} · scade il{' '}
                  {formatDateTime(invitation.expiresAt)}
                </p>
              </div>

              {/* Il link non è ripetibile dal pannello: il token esce solo
                nell'email, così chi ha `member:invite` non può fabbricarne uno e
                usarlo altrove. Per rimandarlo si invita di nuovo lo stesso
                indirizzo — il server revoca da sé l'invito precedente. */}
              <Button
                size="sm"
                variant="danger-outline"
                loading={revoke.isPending && revoke.variables === invitation.invitationId}
                onClick={() => void onRevoke(invitation)}
              >
                <MailX />
                Revoca
              </Button>
            </div>
          </Card>
        </li>
      ))}
    </ul>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, index) => (
        <div
          key={index}
          className="border-border bg-card flex items-center gap-3 rounded-md border p-3"
        >
          <Skeleton className="rounded-pill size-9 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-52" />
          </div>
          <Skeleton className="h-6 w-20 shrink-0" />
        </div>
      ))}
    </div>
  )
}
